import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider, CircularProgress, IconButton, Collapse, Button, TextField, Grid, FormControl,
  InputLabel, Select, MenuItem, Stack, Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaymentsWithCheques } from '../slices/financeSlice';
import { bankLabel, branchLabel } from '../utils/bankLabel';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import { useNavigate } from 'react-router-dom';

import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const OutgoingCheques = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { chequesData, chequesSummary, chequesStatus, error } = useSelector((state) => state.finance);
  const [tableRows, setTableRows] = useState([]);
  const [allDates, setAllDates] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Filter states - initialized from localStorage
  const [nameFilter, setNameFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('outgoing_cheques_filters');
      return saved ? JSON.parse(saved).nameFilter || '' : '';
    } catch { return ''; }
  });
  const [dateFromFilter, setDateFromFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('outgoing_cheques_filters');
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed?.dateFrom ? dayjs(parsed.dateFrom) : null;
    } catch { return null; }
  });
  const [dateToFilter, setDateToFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('outgoing_cheques_filters');
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed?.dateTo ? dayjs(parsed.dateTo) : null;
    } catch { return null; }
  });
  const [chequeStatusFilter, setChequeStatusFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('outgoing_cheques_filters');
      return saved ? JSON.parse(saved).chequeStatusFilter || '' : '';
    } catch { return ''; }
  });
  const [searchFilter, setSearchFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('outgoing_cheques_filters');
      return saved ? JSON.parse(saved).searchFilter || '' : '';
    } catch { return ''; }
  });
  const [filteredPayments, setFilteredPayments] = useState([]);

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('outgoing_cheques_filters', JSON.stringify({
      nameFilter,
      dateFrom: dateFromFilter ? dateFromFilter.toISOString() : null,
      dateTo: dateToFilter ? dateToFilter.toISOString() : null,
      chequeStatusFilter,
      searchFilter,
    }));
  }, [nameFilter, dateFromFilter, dateToFilter, chequeStatusFilter, searchFilter]);

  useEffect(() => {
    // Apply filters and fetch data
    applyFiltersAndFetch();
  }, [dispatch]);

  // Apply filters and fetch data from API
  const applyFiltersAndFetch = () => {
    const filters = {};
    
    if (dateFromFilter) {
      filters.start_date = dateFromFilter.format('YYYY-MM-DD');
    }
    if (dateToFilter) {
      filters.end_date = dateToFilter.format('YYYY-MM-DD');
    }
    if (chequeStatusFilter) {
      filters.status = chequeStatusFilter;
    }
    // Remove searchFilter from API call since we'll handle it client-side
    // if (searchFilter) {
    //   filters.search = searchFilter;
    // }

    dispatch(fetchPaymentsWithCheques(filters));
  };

  // Helper function to get the display date (cheque date if exists, otherwise payment date)
  const getDisplayDate = (payment) => {
    if (payment.cheque && payment.cheque.cheque_date) {
      return payment.cheque.cheque_date;
    }
    return payment.date;
  };

  // Helper function to get date type label
  const getDateTypeLabel = (payment) => {
    if (payment.cheque && payment.cheque.cheque_date) {
      return 'تاريخ الشيك';
    }
    return 'تاريخ الدفع';
  };

  // Handle view payment details
  const handleViewPayment = (paymentId) => {
    navigate(`/finance/view/${paymentId}`);
  };

  // Apply client-side filters for target name (since API doesn't support this filter)
  useEffect(() => {
    let filtered = chequesData || [];

    // Apply target name filter (client-side) - matching your existing logic
    if (nameFilter.trim()) {
      filtered = filtered.filter(p => 
        p.target_name && p.target_name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    // Apply cheque search filter (client-side as backup)
    if (searchFilter.trim()) {
      filtered = filtered.filter(p => {
        const searchLower = searchFilter.toLowerCase();
        return (
          (p.cheque?.cheque_number && p.cheque.cheque_number.toLowerCase().includes(searchLower)) ||
          (bankLabel(p.cheque) && bankLabel(p.cheque).toLowerCase().includes(searchLower)) ||
          (branchLabel(p.cheque) && branchLabel(p.cheque).toLowerCase().includes(searchLower)) ||
          (p.cheque?.account_number && p.cheque.account_number.toLowerCase().includes(searchLower)) ||
          (p.reason && p.reason.toLowerCase().includes(searchLower))
        );
      });
    }

    setFilteredPayments(filtered);
  }, [chequesData, nameFilter, searchFilter]);

  // Update table rows when filtered payments change - same logic as your existing component
  useEffect(() => {
    let data = filteredPayments;

    // Apply date range filter client-side for consistency with your existing component
    if (dateFromFilter || dateToFilter) {
      data = data.filter(p => {
        const displayDate = dayjs(getDisplayDate(p));
        
        let passesFromFilter = true;
        let passesToFilter = true;
        
        if (dateFromFilter) {
          passesFromFilter = displayDate.isSameOrAfter(dateFromFilter, 'day');
        }
        
        if (dateToFilter) {
          passesToFilter = displayDate.isSameOrBefore(dateToFilter, 'day');
        }
        
        return passesFromFilter && passesToFilter;
      });
    }

    // Use display date for grouping (cheque date if exists, otherwise payment date)
    const today = dayjs().startOf('day');
    const todayStr = today.format('YYYY-MM-DD');
    const datesWithPayments = Array.from(
      new Set(
        data.map(p => dayjs(getDisplayDate(p)).format('YYYY-MM-DD'))
      )
    );
    
    // Add today's date if not present and no date filters are applied
    if (!datesWithPayments.includes(todayStr) && !dateFromFilter && !dateToFilter) {
      datesWithPayments.push(todayStr);
    }
    datesWithPayments.sort();

    // Group payments by display date
    const rows = datesWithPayments.map(dateStr => {
      const isToday = dateStr === todayStr;
      const payments = data.filter(
        p => dayjs(getDisplayDate(p)).format('YYYY-MM-DD') === dateStr
      );
      let status = '';
      if (isToday) {
        status = payments.length > 0 ? 'today-has' : 'today-empty';
      } else if (dayjs(dateStr).isAfter(today)) {
        status = 'upcoming';
      } else if (dayjs(dateStr).isBefore(today)) {
        status = 'overdue';
      }
      return {
        date: dateStr,
        isToday,
        payments,
        status,
      };
    });

    setTableRows(rows);
    setAllDates(datesWithPayments);
  }, [filteredPayments, dateFromFilter, dateToFilter]);

  const handleExpandClick = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };

  // Clear all filters - same as your existing component
  const handleClearFilters = () => {
    setNameFilter('');
    setDateFromFilter(null);
    setDateToFilter(null);
    setChequeStatusFilter('');
    setSearchFilter('');
    // Refetch data without filters
    // dispatch(fetchPaymentsWithCheques({}));
  };

  // Check if any filters are active
  const hasActiveFilters = nameFilter.trim() || dateFromFilter || dateToFilter || 
                          chequeStatusFilter || searchFilter;

  // Print function with updated data mapping
  const handlePrint = () => {
    const columns = [
      { header: 'رقم الدفعة', key: 'paymentNumber' },
      { header: 'المستفيد', key: 'targetName' },
      { header: 'تاريخ الدفع/الشيك', key: 'displayDate' },
      { header: 'المبلغ (شيكل)', key: 'amount' },
      { header: 'نوع الدفعة', key: 'paymentType' },
      { header: 'رقم الشيك', key: 'chequeNumber' },
      { header: 'حالة الصورة', key: 'imageStatus' },
    ];
    const data = filteredPayments.map(p => ({
      paymentNumber: p.number,
      targetName: p.target_name,
      displayDate: getDisplayDate(p),
      amount: p.amount,
      paymentType: p.payment_type,
      chequeNumber: p.cheque?.cheque_number || '-',
      imageStatus: p.cheque?.cheque_image ? 'يوجد صورة' : 'لا توجد صورة',
    }));
    exportToPrintWindow(data, columns, 'التزمات');
  };

  // Excel export function with updated data mapping
  const handleExportExcel = () => {
    const columns = [
      { header: 'رقم الدفعة', key: 'paymentNumber' },
      { header: 'المستفيد', key: 'targetName' },
      { header: 'تاريخ الدفع/الشيك', key: 'displayDate' },
      { header: 'المبلغ (شيكل)', key: 'amount' },
      { header: 'نوع الدفعة', key: 'paymentType' },
      { header: 'رقم الشيك', key: 'chequeNumber' },
      { header: 'البنك', key: 'bankInfo' },
      { header: 'رقم الحساب', key: 'accountNumber' },
      { header: 'حالة الصورة', key: 'imageStatus' },
      { header: 'السبب', key: 'reason' },
    ];
    const data = filteredPayments.map(p => ({
      paymentNumber: p.number,
      targetName: p.target_name,
      displayDate: getDisplayDate(p),
      amount: p.amount,
      paymentType: p.payment_type,
      chequeNumber: p.cheque?.cheque_number || '-',
      bankInfo: p.cheque ? `${bankLabel(p.cheque)}-${branchLabel(p.cheque)}` : '-',
      accountNumber: p.cheque?.account_number || '-',
      imageStatus: p.cheque?.cheque_image ? 'يوجد صورة' : 'لا توجد صورة',
      reason: p.reason || '-',
    }));
    exportToExcel(data, columns, 'outgoing_cheques');
  };

  // Show loading state
  if (chequesStatus === 'loading') {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            direction: 'rtl',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: '#F8F9FF',
          }}
        >
          <CircularProgress sx={{ color: '#4338CA' }} />
        </Box>
      </LocalizationProvider>
    );
  }

  // Show error state
  if (error) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            direction: 'rtl',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: '#F8F9FF',
          }}
        >
          <Typography sx={{ color: '#DC2626', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            خطأ: {error}
          </Typography>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    // 🎨 Design System Applied
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          p: { xs: 2, md: 3 },
          bgcolor: '#F8F9FF',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          {/* Header Section */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 2.5,
                  width: 52,
                  height: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLongIcon sx={{ color: '#fff', fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="#fff"
                  sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  التزامات المدرسة
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    mt: 0.25,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  متابعة دفعات وشيكات المدرسة حسب التاريخ والحالة
                </Typography>
              </Box>
            </Stack>

            {chequesSummary && (
              <Stack useFlexGap
                direction="row"
                spacing={1.5}
                sx={{ flexWrap: 'wrap', gap: 1.5, justifyContent: 'flex-end' }}
              >
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2.5,
                    px: 2.5,
                    py: 1,
                    textAlign: 'center',
                    minWidth: 120,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    إجمالي الدفعات
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="#fff"
                    sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    {chequesSummary.total_payments || 0}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2.5,
                    px: 2.5,
                    py: 1,
                    textAlign: 'center',
                    minWidth: 120,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    إجمالي المبلغ
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="#fff"
                    sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    {chequesSummary.total_amount?.toLocaleString('en-US') || 0} ₪
                  </Typography>
                </Box>
              </Stack>
            )}
          </MotionPaper>

          {/* Filters Section */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <Box
                sx={{
                  bgcolor: '#EEF2FF',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: 1,
                }}
              >
                <FilterListIcon sx={{ color: '#4338CA', fontSize: 20 }} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                الفلاتر
              </Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{
                    ml: 'auto',
                    color: '#DC2626',
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&:hover': { bgcolor: '#FEE2E2' },
                  }}
                >
                  مسح الفلاتر
                </Button>
              )}
            </Box>

            <Grid container spacing={2.5} sx={{ alignItems: 'flex-end' }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="البحث بالمستفيد"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="أدخل اسم المستفيد"
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      height: '56px',
                      fontSize: '16px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '16px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="البحث في الشيك"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="رقم الشيك، البنك..."
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      height: '56px',
                      fontSize: '16px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '16px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="من تاريخ"
                  value={dateFromFilter}
                  onChange={(newValue) => setDateFromFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': { 
                          height: '56px',
                          fontSize: '16px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '16px'
                        },
                        '& .MuiPickersInputBase-root': {
                          flexDirection: 'row-reverse'
                        },
                        '& .css-1hgcujo-MuiPickersInputBase-root-MuiPickersOutlinedInput-root': {
                          flexDirection: 'row-reverse'
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="إلى تاريخ"
                  value={dateToFilter}
                  onChange={(newValue) => setDateToFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': { 
                          height: '56px',
                          fontSize: '16px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '16px'
                        },
                        '& .MuiPickersInputBase-root': {
                          flexDirection: 'row-reverse'
                        },
                        '& .css-1hgcujo-MuiPickersInputBase-root-MuiPickersOutlinedInput-root': {
                          flexDirection: 'row-reverse'
                        }
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            {/* Filter Results Summary */}
            {hasActiveFilters && (
              <Box
                sx={{
                  mt: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  bgcolor: '#EEF2FF',
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  إجمالي النتائج: {filteredPayments.length} دفعة
                </Typography>
                <Chip
                  label="تم تطبيق الفلاتر"
                  size="small"
                  icon={<FilterListIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: '#E0F2FE',
                    color: '#0284C7',
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '& .MuiChip-icon': { color: '#0284C7' },
                  }}
                />
              </Box>
            )}
            {!hasActiveFilters && (
              <Typography
                variant="body2"
                sx={{ mt: 2.5, color: '#6B7280', fontWeight: 500, fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إجمالي النتائج: {filteredPayments.length} دفعة
              </Typography>
            )}
          </MotionPaper>

          {/* Export and Print buttons */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              mb: 3,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              تصدير القائمة
            </Typography>
            <Stack useFlexGap
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, gap: 1.5 }}
            >
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#16A34A',
                  color: '#16A34A',
                  '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                }}
              >
                تصدير Excel
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handlePrint}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                }}
              >
                طباعة
              </Button>
            </Stack>
          </MotionPaper>

          {/* Results Table */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              mb: 3,
            }}
          >
          <TableContainer sx={{ borderRadius: 3, bgcolor: '#fff' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['التاريخ', 'المستفيد', 'تفاصيل الدفعة', 'تفاصيل إضافية'].map((h) => (
                    <TableCell
                      key={h}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: '#1E1B4B',
                        bgcolor: '#F1F5F9',
                        borderBottom: '1px solid #E5E7EB',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography
                        variant="body1"
                        sx={{ color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      >
                        لا توجد نتائج تطابق الفلاتر المحددة
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRows.map((row) =>
                    row.payments.length === 0 ? null : row.payments.map((p, i) => {
                      // Payment data - adapted for payments instead of recipients
                      const targetName = p.target_name || 'غير معروف';
                      const paymentType = p.payment_type || '-';
                      const entityType = p.type || '-';
                      const rowKey = `${row.date}-${p.id || i}`;
                      const displayDate = getDisplayDate(p);
                      const dateTypeLabel = getDateTypeLabel(p);
                      
                      return (
                        <React.Fragment key={rowKey}>
                          <TableRow
                            sx={{
                              bgcolor: row.isToday
                                ? '#EEF2FF'
                                : row.status === 'upcoming'
                                  ? '#DCFCE7'
                                  : row.status === 'overdue'
                                    ? '#FEE2E2'
                                    : '#fff',
                              borderRight: row.isToday ? '4px solid #4338CA' : undefined,
                              transition: 'background 0.2s',
                              '&:hover': { bgcolor: row.isToday ? '#E0E7FF' : '#F8F9FF' },
                            }}
                          >
                            <TableCell align="center" sx={{
                              fontWeight: row.isToday ? 700 : 500,
                              color: row.isToday ? '#4338CA' : row.status === 'overdue' ? '#DC2626' : row.status === 'upcoming' ? '#16A34A' : '#1E1B4B',
                              fontSize: '0.9rem',
                              borderBottom: '1px solid #E5E7EB',
                              fontFamily: 'Cairo, Tajawal, sans-serif',
                            }}>
                              {row.isToday ? (
                                <span>
                                  اليوم <br />
                                  <span style={{ fontSize: 12, color: '#6B7280' }}>{row.date}</span>
                                </span>
                              ) : (
                                <span>{row.date}</span>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #E5E7EB' }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {targetName}
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                #{p.number} - {entityType}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #E5E7EB' }}>
                              <Chip
                                label={row.status === 'overdue' ? 'متأخرة' : row.status === 'upcoming' ? 'قادمة' : 'اليوم'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  fontFamily: 'Cairo, Tajawal, sans-serif',
                                  bgcolor: row.status === 'overdue' ? '#FEE2E2' : row.status === 'upcoming' ? '#DCFCE7' : '#E0F2FE',
                                  color: row.status === 'overdue' ? '#DC2626' : row.status === 'upcoming' ? '#16A34A' : '#0284C7',
                                }}
                              />
                              <Typography sx={{ mt: 1, fontWeight: 700, color: '#1E1B4B', fontSize: '0.875rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {parseFloat(p.amount).toLocaleString('en-US')} ₪ - {paymentType}
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {dateTypeLabel}: {dayjs(displayDate).format('YYYY-MM-DD')}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #E5E7EB' }}>
                              <Stack useFlexGap direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                {/* View Details Button */}
                                <Tooltip title="عرض التفاصيل">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewPayment(p.id)}
                                    sx={{
                                      color: '#4338CA',
                                      '&:hover': { bgcolor: '#EEF2FF' },
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                {/* Expand Button */}
                                <Tooltip title={expandedRows[rowKey] ? 'إخفاء التفاصيل' : 'عرض المزيد'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleExpandClick(rowKey)}
                                    sx={{
                                      color: '#4338CA',
                                      transition: 'transform 0.2s',
                                      transform: expandedRows[rowKey] ? 'rotate(180deg)' : 'rotate(0deg)',
                                      '&:hover': { bgcolor: '#EEF2FF' },
                                    }}
                                  >
                                    {expandedRows[rowKey] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ p: 0, border: 0, background: 'transparent' }}>
                              <Collapse in={!!expandedRows[rowKey]} timeout="auto" unmountOnExit>
                                <Box sx={{
                                  p: 3,
                                  bgcolor: '#F8F9FF',
                                  borderRadius: 3,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                                  my: 1.5,
                                  mx: 2,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 3,
                                  justifyContent: 'flex-start'
                                }}>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الدفعة</Typography>
                                    <Typography>#{p.number}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>المستفيد</Typography>
                                    <Typography>{targetName}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>نوع المستفيد</Typography>
                                    <Typography>{entityType}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>قيمة الدفعة</Typography>
                                    <Typography>{parseFloat(p.amount).toLocaleString('en-US')} ₪</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>نوع الدفعة</Typography>
                                    <Typography>{paymentType}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 150 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{dateTypeLabel}</Typography>
                                    <Typography>{dayjs(displayDate).format('YYYY-MM-DD')}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>السبب</Typography>
                                    <Typography>{p.reason || '-'}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 150 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>منشئ الدفعة</Typography>
                                    <Typography>{p.created_by_name || '-'}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>السنة الدراسية</Typography>
                                    <Typography>{p.school_year_label || '-'}</Typography>
                                  </Box>
                                  {/* Show original payment date if cheque date is different */}
                                  {p.cheque && p.cheque.cheque_date !== p.date && (
                                    <Box sx={{ minWidth: 150 }}>
                                      <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>تاريخ الدفع الأصلي</Typography>
                                      <Typography>{dayjs(p.date).format('YYYY-MM-DD')}</Typography>
                                    </Box>
                                  )}
                                  {/* Show cheque details if available */}
                                  {p.cheque && (
                                    <>
                                      <Box sx={{ minWidth: 120 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>البنك</Typography>
                                        <Typography>{bankLabel(p.cheque) || '-'}</Typography>
                                      </Box>
                                      <Box sx={{ minWidth: 120 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الفرع</Typography>
                                        <Typography>{branchLabel(p.cheque) || '-'}</Typography>
                                      </Box>
                                      <Box sx={{ minWidth: 150 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الحساب</Typography>
                                        <Typography>{p.cheque.account_number || '-'}</Typography>
                                      </Box>
                                      {p.cheque.cheque_number && (
                                        <Box sx={{ minWidth: 120 }}>
                                          <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الشيك</Typography>
                                          <Typography>{p.cheque.cheque_number}</Typography>
                                        </Box>
                                      )}
                                      {p.cheque.description && (
                                        <Box sx={{ minWidth: 200 }}>
                                          <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>وصف الشيك</Typography>
                                          <Typography>{p.cheque.description}</Typography>
                                        </Box>
                                      )}
                                      {p.cheque.cheque_image && (
                                        <Box sx={{ minWidth: 200 }}>
                                          <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.8rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>صورة الشيك</Typography>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ImageIcon />}
                                            onClick={() => window.open(p.cheque.cheque_image_url || p.cheque.cheque_image, '_blank')}
                                            sx={{
                                              mt: 1,
                                              borderRadius: 2.5,
                                              textTransform: 'none',
                                              fontWeight: 700,
                                              fontFamily: 'Cairo, Tajawal, sans-serif',
                                              borderColor: '#4338CA',
                                              color: '#4338CA',
                                              '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                                            }}
                                          >
                                            عرض الصورة
                                          </Button>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </MotionPaper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default OutgoingCheques;